import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Button, Descriptions, Tag, Timeline,
    Modal, message, List
} from 'antd';
import {
    DollarOutlined,
    EyeOutlined,
    ProfileOutlined,
    FileTextOutlined,
    FilePdfOutlined
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import API from '../../../API/API';
// import './LeadDetails.css';
import { CircleCheckBig } from 'lucide-react';

const calculateTotalPaid = (leadData) => {
    if (!leadData?.advancePayments) return 0;
    return leadData.advancePayments.reduce((sum, payment) => {
        if (payment.status === 'Completed' || payment.status === undefined) {
            return sum + (payment.amount || 0);
        }
        return sum;
    }, 0);
};

const calculateBalance = (leadData) => {
    const total = leadData?.totalAmount || 0;
    const paid = calculateTotalPaid(leadData);
    return Math.max(0, total - paid);
};

const ZohoSignModal = ({ visible, onCancel, onSend, lead, userData }) => {
    const [loading, setLoading] = useState(false);
    const [previewMode, setPreviewMode] = useState(true);
    const [emailContent, setEmailContent] = useState({
        subject: '',
        text: '',
        html: ''
    });
    const [zohoStatus, setZohoStatus] = useState(
        lead.zohoStatus || 'NOT-SENT'
    );

    const [isDownloading, setIsDownloading] = useState(false);

    // Update Tag color mapping
    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED': return 'green';
            case 'SENT': return 'blue';
            case 'PROCESSING': return 'orange';
            case 'INCOMPLETE': return 'red';
            case 'FAILED': return 'red';
            default: return 'default';
        }
    };
    const statusInterval = useRef(null);

    // Prepare data for PDF and email
    const prepareData = () => {
        const billingInfo = {
            fullName: lead.personalInfo?.fullName || "",
            email: lead.personalInfo?.email || "",
            phoneNumber: lead.personalInfo?.phoneNumber || "",
            street: lead.billingAddress?.street || "",
            city: lead.billingAddress?.city || "",
            state: lead.billingAddress?.state || "",
            zipCode: lead.billingAddress?.zip || "",
            businessName: lead.bussinessName || ""
        };

        const paymentInfo = {
            total: lead.totalAmount || 0,
            paid: calculateTotalPaid(lead),
            balance: calculateBalance(lead),
            advancePayments: lead.advancePayments || []
        };

        const shippingInfo = {
            street: lead.shippingAddress?.street || "",
            city: lead.shippingAddress?.city || "",
            state: lead.shippingAddress?.state || "",
            zipCode: lead.shippingAddress?.zip || "",
            attnName: lead.attnName || lead.personalInfo?.fullName || "",
            businessName: lead.bussinessName || ""
        };

        const orderInfo = {
            orderId: lead.orderNumber || `ORDER-${lead._id.slice(-8).toUpperCase()}`,
            orderDate: lead.orderDate || new Date().toISOString(),
            partDescription: `${lead.vehicleInfo?.year || ''} ${lead.vehicleInfo?.make || ''} ${lead.vehicleInfo?.model || ''} ${lead.vehicleInfo?.part || ''}`,
            warranty: lead.warranty || "Standard 30-Day Warranty"
        };

        return { billingInfo, paymentInfo, shippingInfo, orderInfo };
    };

    const { billingInfo, paymentInfo, shippingInfo, orderInfo } = prepareData();

    // Get latest payment
    const latestPayment = paymentInfo.advancePayments[paymentInfo.advancePayments.length - 1];

    // Initialize email content when modal opens
    useEffect(() => {
        if (visible) {
            const subject = `Sales Agreement - Order ${orderInfo.orderId}`;

            // Generate email text content
            const text = `Dear ${billingInfo.fullName},

Please find your Sales Agreement and Payment Receipt for Order ${orderInfo.orderId}.

ORDER DETAILS:
• Order Number: ${orderInfo.orderId}
• Part: ${orderInfo.partDescription}
• Order Date: ${new Date(orderInfo.orderDate).toLocaleDateString()}
• Warranty: ${orderInfo.warranty}

PAYMENT SUMMARY:
• Total Amount: $${paymentInfo.total}
• Total Paid: $${paymentInfo.paid}
${latestPayment ? `• Latest Payment (${new Date(latestPayment.date).toLocaleDateString()}): $${latestPayment.amount} via ${latestPayment.paymentMethod}` : ''}
• Balance Due: $${paymentInfo.balance}

Please review the payment details above and let us know if you have any questions.

Thank you for your business!

Best regards,
${userData?.firstName} ${userData?.lastName}
Premium Used Auto Parts (LLC)
1-800-606-1107
sales@premiumusedautoparts.com`;

            // Generate HTML content
            const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        body { 
            font-family: 'Arial', sans-serif; 
            line-height: 1.6; 
            color: #333;
            margin: 0;
            padding: 20px;
            font-size: 12px;
        }
        .header { 
            background: #f8f9fa; 
            padding: 15px; 
            border-left: 4px solid #1890ff; 
            margin-bottom: 20px; 
        }
        .section { 
            margin-bottom: 15px; 
            page-break-inside: avoid;
        }
        .section-title {
            color: #1890ff;
            border-bottom: 2px solid #1890ff;
            padding-bottom: 5px;
            margin-bottom: 10px;
            font-size: 14px;
        }
        .payment-summary { 
            background: #f6ffed; 
            padding: 12px; 
            border-radius: 5px; 
            margin: 10px 0; 
            border-left: 3px solid #52c41a;
        }
        .highlight { 
            background: #e6f7ff; 
            padding: 8px; 
            border-left: 3px solid #1890ff; 
            margin: 8px 0; 
        }
        .footer { 
            margin-top: 20px; 
            padding-top: 15px; 
            border-top: 1px solid #eee; 
            font-size: 11px; 
            color: #666; 
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 11px;
        }
        th {
            background: #f0f0f0;
            padding: 6px;
            text-align: left;
            border: 1px solid #ddd;
            font-weight: bold;
        }
        td {
            padding: 6px;
            border: 1px solid #ddd;
        }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-bold { font-weight: bold; }
        .text-success { color: #52c41a; }
        .text-danger { color: #f5222d; }
        .mb-5 { margin-bottom: 10px; }
        .mt-5 { margin-top: 10px; }
        .page-break {
            page-break-before: always;
        }
        .logo {
            font-size: 16px;
            font-weight: bold;
            color: #1890ff;
            margin-bottom: 5px;
        }
    </style>
</head>
<body>
    <div className="header">
        <div className="logo">Premium Used Auto Parts (LLC)</div>
        <div>10940 White Rock Rd, Suite #210, Rancho Cordova, CA 95670</div>
    </div>

    <div className="section">
        <h3 style="margin: 0 0 15px 0;">Sales Agreement</h3>
        <div className="section-title">Order Details</div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;">
            <div><strong>Order Number:</strong> ${orderInfo.orderId}</div>
            <div><strong>Order Date:</strong> ${new Date(orderInfo.orderDate).toLocaleDateString()}</div>
            <div><strong>Customer:</strong> ${billingInfo.fullName}</div>
            <div><strong>Email:</strong> ${billingInfo.email}</div>
            <div><strong>Part Description:</strong> ${orderInfo.partDescription}</div>
            <div><strong>Warranty:</strong> ${orderInfo.warranty}</div>
        </div>
    </div>

    <div className="section">
        <div className="section-title">Payment Summary</div>
        <div className="payment-summary">
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                <div><strong>Total Amount:</strong></div>
                <div className="text-right">$${paymentInfo.total}</div>
                <div><strong>Total Paid:</strong></div>
                <div className="text-right text-success text-bold">$${paymentInfo.paid}</div>
                <div><strong>Balance Due:</strong></div>
                <div className="text-right ${paymentInfo.balance > 0 ? 'text-danger' : 'text-success'} text-bold">
                    $${paymentInfo.balance}
                </div>
            </div>
        </div>
    </div>

    ${latestPayment ? `
    <div className="section">
        <div className="section-title">Latest Payment Details</div>
        <div className="highlight">
            <div><strong>Amount:</strong> $${latestPayment.amount}</div>
            <div><strong>Date:</strong> ${new Date(latestPayment.date).toLocaleDateString()}</div>
            <div><strong>Payment Method:</strong> ${latestPayment.paymentMethod}</div>
            ${latestPayment.transactionId ? `<div><strong>Transaction ID:</strong> ${latestPayment.transactionId}</div>` : ''}
        </div>
    </div>
    ` : ''}

    ${paymentInfo.advancePayments.length > 0 ? `
    <div className="section">
        <div className="section-title">Payment History</div>
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${paymentInfo.advancePayments.map((payment, index) => `
                <tr style="${index === paymentInfo.advancePayments.length - 1 ? 'background: #f6ffed;' : ''}">
                    <td>${new Date(payment.date).toLocaleDateString()}</td>
                    <td className="${index === paymentInfo.advancePayments.length - 1 ? 'text-success text-bold' : ''}">
                        $${payment.amount}
                    </td>
                    <td>${payment.paymentMethod}</td>
                    <td>${payment.status || 'Completed'}</td>
                </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
    ` : ''}

    ${(billingInfo.street || billingInfo.businessName) ? `
    <div className="section">
        <div className="section-title">Business Information</div>
        ${billingInfo.businessName ? `<div><strong>Business Name:</strong> ${billingInfo.businessName}</div>` : ''}
        ${billingInfo.street ? `
        <div className="mb-5">
            <strong>Billing Address:</strong><br>
            ${billingInfo.street}<br>
            ${billingInfo.city}, ${billingInfo.state} ${billingInfo.zipCode}
        </div>
        ` : ''}
    </div>
    ` : ''}

    <div className="footer">
        <div className="text-center mb-5">
            <strong>Thank you for your business!</strong>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
            <div>
                <strong>Best regards,</strong><br>
                ${userData?.firstName} ${userData?.lastName}<br>
                Premium Used Auto Parts (LLC)
            </div>
            <div>
                <strong>Contact Information:</strong><br>
                📞 Phone: 1-800-606-1107<br>
                📧 Email: sales@premiumusedautoparts.com<br>
                🌐 Website: premiumusedautoparts.com
            </div>
        </div>
        <div className="text-center mt-10" style="font-size: 10px; color: #999;">
            Document generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
        </div>
    </div>
</body>
</html>`;

            setEmailContent({ subject, text, html });
        }
    }, [visible, lead, userData]);

    // PDF Download Function
    const handleDownloadPDF = async () => {
        let pdfContainer;
        try {
            setIsDownloading(true);
            const ZOHO_TARGET_PDF_SIZE_MB = 20;
            const ZOHO_MAX_PDF_SIZE_MB = 25;
            const getPdfSizeInMb = (pdf) => {
                const pdfArrayBuffer = pdf.output('arraybuffer');
                return pdfArrayBuffer.byteLength / (1024 * 1024);
            };

            // Create a temporary div for PDF generation
            pdfContainer = document.createElement('div');
            pdfContainer.style.position = 'absolute';
            pdfContainer.style.left = '-9999px';
            pdfContainer.style.width = '794px'; // A4 width in pixels at 96 DPI
            pdfContainer.style.padding = '20px';
            pdfContainer.style.backgroundColor = 'white';

            // Set the HTML content
            pdfContainer.innerHTML = emailContent.html;
            document.body.appendChild(pdfContainer);

            const generatePdfForQuality = async (jpegQuality) => {
                const pdf = new jsPDF({
                    orientation: 'p',
                    unit: 'mm',
                    format: 'a4',
                    compress: true,
                });
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.25,
                    useCORS: true,
                    logging: false,
                    width: 794,
                    windowWidth: 794,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
                const imgWidth = pdfWidth;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                let heightLeft = imgHeight;
                let position = 0;

                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;

                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pdfHeight;
                }

                return {
                    pdf,
                    pdfSizeMB: getPdfSizeInMb(pdf),
                };
            };

            let pdfResult = await generatePdfForQuality(0.7);

            if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
                pdfResult = await generatePdfForQuality(0.6);
            }

            if (pdfResult.pdfSizeMB > ZOHO_MAX_PDF_SIZE_MB) {
                message.error("PDF is larger than Zoho Sign's 25 MB limit. Please reduce document size and try again.");
                return;
            }

            pdfResult.pdf.save(`Sales-Agreement-${orderInfo.orderId}.pdf`);

            if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
                message.info(`PDF downloaded at ${pdfResult.pdfSizeMB.toFixed(2)} MB. It is above the 20 MB target but still under Zoho's 25 MB limit.`);
                return;
            }

            message.success(`PDF downloaded successfully! (${pdfResult.pdfSizeMB.toFixed(2)} MB)`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Failed to download PDF. Please try again.');
        } finally {
            if (pdfContainer?.parentNode) {
                pdfContainer.parentNode.removeChild(pdfContainer);
            }
            setIsDownloading(false);
        }
    };

    // Alternative: Generate PDF with pure text (if HTML conversion has issues)
    const handleDownloadTextPDF = () => {
        try {
            setIsDownloading(true);

            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin = 20;
            const lineHeight = 7;
            let yPos = margin;

            // Add header
            pdf.setFontSize(16);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Premium Used Auto Parts (LLC)', pageWidth / 2, yPos, { align: 'center' });
            yPos += lineHeight;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text('10940 White Rock Rd, Suite #210, Rancho Cordova, CA 95670', pageWidth / 2, yPos, { align: 'center' });
            yPos += lineHeight * 2;

            // Add title
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.text('SALES AGREEMENT', pageWidth / 2, yPos, { align: 'center' });
            yPos += lineHeight * 2;

            // Add order details
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Order Details:', margin, yPos);
            yPos += lineHeight;

            pdf.setFont('helvetica', 'normal');
            pdf.text(`Order Number: ${orderInfo.orderId}`, margin, yPos);
            yPos += lineHeight;
            pdf.text(`Order Date: ${new Date(orderInfo.orderDate).toLocaleDateString()}`, margin, yPos);
            yPos += lineHeight;
            pdf.text(`Customer: ${billingInfo.fullName}`, margin, yPos);
            yPos += lineHeight;
            pdf.text(`Part: ${orderInfo.partDescription}`, margin, yPos);
            yPos += lineHeight * 1.5;

            // Add payment summary
            pdf.setFont('helvetica', 'bold');
            pdf.text('Payment Summary:', margin, yPos);
            yPos += lineHeight;

            pdf.setFont('helvetica', 'normal');
            pdf.text(`Total Amount: $${paymentInfo.total}`, margin, yPos);
            yPos += lineHeight;
            pdf.text(`Total Paid: $${paymentInfo.paid}`, margin, yPos);
            yPos += lineHeight;
            pdf.text(`Balance Due: $${paymentInfo.balance}`, margin, yPos);
            yPos += lineHeight * 1.5;

            // Add payment history if exists
            if (paymentInfo.advancePayments.length > 0) {
                pdf.setFont('helvetica', 'bold');
                pdf.text('Payment History:', margin, yPos);
                yPos += lineHeight;

                paymentInfo.advancePayments.forEach((payment, index) => {
                    const paymentText = `${new Date(payment.date).toLocaleDateString()} - $${payment.amount} via ${payment.paymentMethod}`;
                    pdf.setFont('helvetica', index === paymentInfo.advancePayments.length - 1 ? 'bold' : 'normal');
                    pdf.text(paymentText, margin + 5, yPos);
                    yPos += lineHeight;
                });
                yPos += lineHeight;
            }

            // Add footer
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'italic');
            pdf.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });
            yPos += lineHeight;
            pdf.text(`Generated by ${userData?.firstName} ${userData?.lastName} on ${new Date().toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });

            // Save the PDF
            pdf.save(`Sales-Agreement-${orderInfo.orderId}.pdf`);

            message.success('PDF downloaded successfully!');

        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Failed to download PDF. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    // Start polling when document is sent
    const startPollingStatus = (requestId) => {
        if (statusInterval.current) clearInterval(statusInterval.current);

        statusInterval.current = setInterval(async () => {
            try {
                const res = await API.GetZohoDocumentStatus(requestId);
                if (res.success) {
                    setZohoStatus(res.status);
                    if (res.status === 'COMPLETED') {
                        clearInterval(statusInterval.current);
                        message.success('Document has been signed!');
                    }
                    if (['INCOMPLETE', 'FAILED'].includes(res.status)) {
                        clearInterval(statusInterval.current);
                        message.error('Zoho Sign was not completed.');
                    }
                }
            } catch (error) {
                console.error('Error fetching Zoho status:', error);
            }
        }, 5000);
    };

    useEffect(() => {
        if (!visible && statusInterval.current) {
            clearInterval(statusInterval.current);
        }
    }, [visible]);

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProfileOutlined />
                    <span>Sales Agreement Preview</span>
                    <Tag color="blue">Preview Only</Tag>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width={1200}
            style={{ top: 20 }}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="download"
                    type="primary"
                    onClick={handleDownloadPDF}
                    loading={isDownloading}
                    icon={<FilePdfOutlined />}
                >
                    Download PDF
                </Button>
            ]}
        >
            <div style={{ display: 'flex', gap: 20 }}>
                {/* Left Panel - Document Preview */}
                <div style={{ flex: 3 }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Document Preview</span>
                                <Button
                                    type="link"
                                    onClick={() => setPreviewMode(!previewMode)}
                                    icon={previewMode ? <FileTextOutlined /> : <FilePdfOutlined />}
                                >
                                    {previewMode ? 'Switch to Text View' : 'Switch to HTML View'}
                                </Button>
                            </div>
                        }
                    >
                        <div
                            id="pdf-content"
                            style={{
                                border: '1px solid #f0f0f0',
                                height: '650px',
                                overflow: 'auto',
                                backgroundColor: '#ffffff',
                                padding: '20px'
                            }}
                        >
                            {previewMode ? (
                                <div dangerouslySetInnerHTML={{ __html: emailContent.html }} />
                            ) : (
                                <pre style={{
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    lineHeight: '1.5'
                                }}>
                                    {emailContent.text}
                                </pre>
                            )}
                        </div>
                        <div style={{ marginTop: 10, textAlign: 'center', fontSize: '12px', color: '#666' }}>
                            Note: This preview may differ slightly from the downloaded PDF
                        </div>
                    </Card>
                </div>

                {/* Right Panel - Details & Actions */}
                <div style={{ flex: 1 }}>
                    <Card title="Document Details" style={{ marginBottom: 20 }}>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Zoho Status:">
                                <div>
                                    <Tag color={getStatusColor(zohoStatus)}>{zohoStatus}</Tag>
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="Recipient">
                                <div>
                                    <div>{billingInfo.fullName}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>{billingInfo.email}</div>
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Number">
                                <Tag color="blue">{orderInfo.orderId}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Agent">
                                <div>
                                    <div>{userData?.firstName} {userData?.lastName}</div>
                                    <div style={{ fontSize: '11px', color: '#666' }}>{userData?.email}</div>
                                </div>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Rest of your existing right panel content remains the same */}
                    <Card title="Payment Summary" size="small">
                        <List size="small">
                            <List.Item>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span>Total Amount:</span>
                                    <span style={{ fontWeight: 'bold' }}>${paymentInfo.total}</span>
                                </div>
                            </List.Item>
                            <List.Item>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span>Total Paid:</span>
                                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                        ${paymentInfo.paid}
                                    </span>
                                </div>
                            </List.Item>
                            {latestPayment && (
                                <List.Item style={{
                                    backgroundColor: '#f6ffed',
                                    borderRadius: 4,
                                    borderLeft: '3px solid #52c41a'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                            🔴 Latest Payment:
                                        </span>
                                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                            ${latestPayment.amount}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                                        {new Date(latestPayment.date).toLocaleDateString()} • {latestPayment.paymentMethod}
                                    </div>
                                    {latestPayment.transactionId && (
                                        <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                                            ID: {latestPayment.transactionId}
                                        </div>
                                    )}
                                </List.Item>
                            )}

                            <List.Item>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span>Balance Due:</span>
                                    <span style={{
                                        color: paymentInfo.balance > 0 ? '#f5222d' : '#52c41a',
                                        fontWeight: 'bold'
                                    }}>
                                        ${paymentInfo.balance}
                                    </span>
                                </div>
                            </List.Item>
                        </List>
                    </Card>

                    <Card title="Payment History" size="small" style={{ marginTop: 20 }}>
                        {paymentInfo.advancePayments.length > 0 ? (
                            <Timeline>
                                {paymentInfo.advancePayments.map((payment, index) => (
                                    <Timeline.Item
                                        key={index}
                                        color={index === paymentInfo.advancePayments.length - 1 ? 'green' : 'blue'}
                                        dot={index === paymentInfo.advancePayments.length - 1 ? <DollarOutlined /> : null}
                                    >
                                        <div>
                                            <div style={{ fontWeight: index === paymentInfo.advancePayments.length - 1 ? 'bold' : 'normal' }}>
                                                ${payment.amount}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#666' }}>
                                                {new Date(payment.date).toLocaleDateString()} • {payment.paymentMethod}
                                            </div>
                                        </div>
                                    </Timeline.Item>
                                ))}
                            </Timeline>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '10px', color: '#999' }}>
                                No payments recorded
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </Modal>
    );
};

export default ZohoSignModal;
